import React, { KeyboardEvent, useRef } from 'react';
import { Box } from '@mui/material';

interface Props {
	className?: string;
	prefix?: string;
	suffix?: string;
	children: React.ReactNode;
	onChange: (value: string) => void;
}

export function TreeNodeInput({
	className,
	prefix,
	suffix,
	onChange,
	children,
}: Props) {
	const ref = useRef<HTMLDivElement>(null);

	const addPrefix = (text: string) => {
		if (!prefix) return text;

		if (text.includes(prefix)) {
			return (
				prefix +
				text
					.split('')
					.filter((char) => char !== prefix)
					.join('')
			);
		}
		return `${prefix} ${text}`;
	};

	const addSuffix = (text: string) => {
		if (!suffix) return text;

		if (text.includes(suffix)) {
			return (
				text
					.split('')
					.filter((char) => char !== suffix)
					.join('') + suffix
			);
		}
		return `${text}${suffix}`;
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		const isNumberKey = /^[0-9]$/.test(e.key);
		if (
			e.key === 'Backspace' ||
			e.key === 'ArrowLeft' ||
			e.key === 'ArrowRight'
		) {
			// Do nothing
		} else if (e.key === 'Enter' && e.currentTarget.textContent) {
			e.preventDefault();
			e.currentTarget.textContent = addPrefix(e.currentTarget.textContent);
			e.currentTarget.textContent = addSuffix(e.currentTarget.textContent);
			onChange(e.currentTarget.textContent);
			if (ref.current) {
				ref.current.blur();
			}
		} else if (!isNumberKey) {
			e.preventDefault();
		}
	};

	const handleBlur = (e: { target: { innerText: string } }) => {
		e.target.innerText = addPrefix(e.target.innerText);
		e.target.innerText = addSuffix(e.target.innerText);
		onChange(e.target.innerText);
	};

	return (
		<Box
			component="span"
			className={className}
			contentEditable={true}
			onKeyDown={handleKeyDown}
			ref={ref}
			onBlur={handleBlur}
			suppressContentEditableWarning={true}
		>
			{children}
		</Box>
	);
}
